import { useCallback, useMemo } from 'react';

import {
  CheckoutEntryController,
  CheckoutEntryDetails,
  CheckoutEntrySelection,
} from '../types';
import { shouldAuthorizePayableAmount } from '../utils/fundLock';
import {
  buildLinkedDrawLineItems,
  sameLinkedDrawLineItems,
} from '../utils/linkedIntent';
import { buildQuoteOrderTotals } from '../utils/orderTotals';
import {
  ConfirmedLinkedDrawEntryIntent,
  useLinkedDrawEntryConfirmation,
} from './useLinkedDrawEntryConfirmation';
import { useLinkedDrawEntryIntent } from './useLinkedDrawEntryIntent';

export type UseLinkedDrawEntryArgs = {
  drawCloseAt: string | null;
  multisetID: string | null;
  onConfirmed: (intent: ConfirmedLinkedDrawEntryIntent) => Promise<void>;
  productIdByDrawId: Map<string, string>;
};

export const useLinkedDrawEntry = ({
  drawCloseAt,
  multisetID,
  onConfirmed,
  productIdByDrawId,
}: UseLinkedDrawEntryArgs): CheckoutEntryController => {
  const {
    clientSecret,
    error,
    intent,
    loading,
    reset,
    submit: submitIntent,
    submittedInput,
  } = useLinkedDrawEntryIntent();
  const { confirmEntry, confirming } = useLinkedDrawEntryConfirmation({
    onConfirmed,
  });

  const toLineItems = useCallback(
    (selection: CheckoutEntrySelection) => {
      return buildLinkedDrawLineItems({
        framingSelections: selection.framingSelections,
        productIdByDrawId,
        selectedDraws: selection.selectedDraws,
      });
    },
    [productIdByDrawId],
  );

  const covers = useCallback(
    (selection: CheckoutEntrySelection) => {
      if (!submittedInput) return true;

      return (
        submittedInput.maxQuantity === selection.maxQuantity &&
        sameLinkedDrawLineItems(
          submittedInput.lineItems,
          toLineItems(selection),
        )
      );
    },
    [submittedInput, toLineItems],
  );

  const submit = useCallback(
    async (
      selection: CheckoutEntrySelection,
      details: CheckoutEntryDetails,
    ) => {
      if (!multisetID || selection.selectedDraws.length === 0) return false;

      const created = await submitIntent({
        multisetID,
        currencyCode: details.currencyCode,
        maxQuantity: selection.maxQuantity,
        shippingAddressId: details.shippingAddressId,
        paymentMethodId: details.paymentMethodId,
        preOrder: details.preOrder,
        authorizePayableAmount: shouldAuthorizePayableAmount({
          drawCloseAt,
          preOrder: details.preOrder,
        }),
        lineItems: toLineItems(selection),
        customAttributes: details.customAttributes,
      });

      return Boolean(created);
    },
    [drawCloseAt, multisetID, submitIntent, toLineItems],
  );

  const quote = useMemo(() => {
    return intent ? buildQuoteOrderTotals(intent.quote) : null;
  }, [intent]);

  return useMemo(
    () => ({
      clientSecret,
      confirmEntry,
      confirming,
      covers,
      error,
      intentId: intent?.id ?? null,
      loading,
      quote,
      quoteCurrencyCode: intent?.quote.currencyCode ?? null,
      reset,
      submit,
    }),
    [
      clientSecret,
      confirmEntry,
      confirming,
      covers,
      error,
      intent,
      loading,
      quote,
      reset,
      submit,
    ],
  );
};
